import * as React from 'react';
import { UitkExpando } from 'uitk-react-expando';
import { observer } from 'mobx-react';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CheckboxInput } from 'components/common/search-form/fields/checkbox-input';
import { FilterModel, FilterOptionModel } from 'components/common/search-form/fields/filter-types';
import { FormFieldBinding } from 'components/common/search-form/fields/form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form/index';
import { CollapseHeight } from 'constants/collapse-height';

export interface CheckboxGroupProps extends SearchFormFieldProps, LocalizedComponentProps {
  getFilterCategory?: (option: FilterOptionModel) => string;
  isChecked?: (option: FilterOptionModel) => boolean;
  handleChange?: (evt) => void;
}

export interface CheckboxGroupState {
  viewExpanded: boolean;
}

@FormFieldBinding
@withLocalization
@observer
export class CheckboxGroup extends React.Component<CheckboxGroupProps, CheckboxGroupState> {
  constructor(props) {
    super(props);

    this.state = { viewExpanded: false };

    this.handleChange = this.handleChange.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);
  }

  private handleToggleClick() {
    this.setState(prevState => ({
      viewExpanded: !prevState.viewExpanded
    }));
  }

  private handleChange(evt) {
    const target = evt.target;
    const { value, checked } = target;

    const { model, modelKey, sortAndFilterOptions, updateModel } = this.props;

    const checkbox: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const checkboxOptions: FilterOptionModel[] = checkbox && checkbox.options;
    const checkboxName: string = checkbox && checkbox.name;
    const modelValue = model && model[checkboxName];
    const isRange = checkbox && checkbox.range;

    if (typeof updateModel !== 'function' || modelValue === undefined) {
      return;
    }

    /* add/remove checked/unchecked values from the array */
    let newArrayValue = (typeof modelValue === 'string' && modelValue.split(',')) || modelValue.slice();
    const index = newArrayValue.indexOf(value);
    if (checked && index === -1) {
      newArrayValue.push(value);
    } else if (!checked && index !== -1) {
      newArrayValue = newArrayValue.filter(item => item !== value);
    } else {
      // The unlikely scenario where submitted value is already where it should be.
      return;
    }

    /* Update first/last value when isRange true e.g. price */
    if (isRange && newArrayValue.length > 0) {
      const { firstIndex, lastIndex } = this.getFirstAndLastIndices(checkboxOptions, newArrayValue);
      newArrayValue = [checkboxOptions[firstIndex].value, checkboxOptions[lastIndex].value];
    }

    updateModel({
      target: {
        name: checkboxName,
        value: newArrayValue
      }
    });
  }

  private getFirstAndLastIndices(checkboxOptions, modelValues) {
    let firstIndex;
    let lastIndex;

    checkboxOptions.forEach((option, i) => {
      const optionChecked = modelValues.includes(option.value);

      if (firstIndex === undefined && optionChecked) {
        firstIndex = i;
      }
      if (optionChecked) {
        lastIndex = i;
      }
    });

    return { firstIndex, lastIndex };
  }

  public render() {
    const { getFilterCategory, isChecked, model, modelKey, sortAndFilterOptions, l10n } = this.props;
    const { viewExpanded } = this.state;

    const checkbox = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const checkboxOptions = checkbox && checkbox.options;
    const hiddenThreshold = checkbox && checkbox.hiddenThreshold;
    const collapseHeight = hiddenThreshold && hiddenThreshold === 5 ? CollapseHeight.FIVE_HEIGHT : CollapseHeight.TEN_HEIGHT;

    if (!checkboxOptions || checkboxOptions.length === 0) {
      return null;
    }

    return (
      <fieldset>
        <legend className="filter-title uitk-type-heading-400 uitk-type-bold">{checkbox.label}</legend>
        {hiddenThreshold && checkboxOptions.length > hiddenThreshold ? (
          <UitkExpando
            className="pwa-switch-group"
            collapseHeight={collapseHeight}
            isVisible={viewExpanded}
            handleClick={this.handleToggleClick}
            expandLabel={l10n.formatText('contentToggle.expand.label')}
            collapseLabel={l10n.formatText('contentToggle.collapse.label')}
          >
            <Checkboxes
              getFilterCategory={getFilterCategory}
              getFirstAndLastIndices={this.getFirstAndLastIndices}
              handleChange={this.props.handleChange || this.handleChange}
              isChecked={isChecked}
              model={model}
              modelKey={modelKey}
              sortAndFilterOptions={sortAndFilterOptions}
            />
          </UitkExpando>
        ) : (
          <div className="pwa-switch-group">
            <Checkboxes
              getFilterCategory={getFilterCategory}
              getFirstAndLastIndices={this.getFirstAndLastIndices}
              handleChange={this.props.handleChange || this.handleChange}
              isChecked={isChecked}
              model={model}
              modelKey={modelKey}
              sortAndFilterOptions={sortAndFilterOptions}
            />
          </div>
        )}
      </fieldset>
    );
  }
}

const Checkboxes = observer(
  ({ getFilterCategory, getFirstAndLastIndices, handleChange, isChecked, model, modelKey, sortAndFilterOptions }) => {
    const checkbox: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const checkboxOptions: FilterOptionModel[] = checkbox && checkbox.options;
    const checkboxName: string = checkbox && checkbox.name;
    const isRange = checkbox && checkbox.range;

    const values = (model && model[checkboxName]) || [];

    const { firstIndex = {}, lastIndex = {} } = (isRange && getFirstAndLastIndices(checkboxOptions, values)) || {};

    return (
      <React.Fragment>
        {checkboxOptions.map((option, i) => (
          <CheckboxInput
            key={`${checkboxName}-${i}`}
            index={i}
            checked={typeof isChecked === 'function' ? isChecked(option) : values.includes(option.value)}
            handleChange={handleChange}
            firstIndex={firstIndex}
            lastIndex={lastIndex}
            name={checkboxName}
            ariaLabel={option.ariaLabel}
            label={option.label}
            value={option.value}
            isRange={isRange}
            description={option.description}
            count={option.count} // Exp 28369: PWA_show_count_of_properties_on_filter_selection
            filterCategory={getFilterCategory && getFilterCategory(option)}
          />
        ))}
      </React.Fragment>
    );
  }
);
